//Dependencies............................................
import React from "react";
import axios from 'axios';
import {Button, Form, Input, Modal} from 'antd';
import Select from "react-select";

//Components..............................................
import {BASE_URL} from '../../../utils/constants';
import {FirmwareBase} from "../../../utils/interfaces";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NotificationInstance } from "antd/es/notification/interface";
import { Control, Controller, FieldErrors, UseFormGetValues, UseFormReset } from "react-hook-form";
import PatchNotesEditor from "../CustomWidgets/PatchNotesEditor";

interface Props {
    showEditFirmwareModal: boolean,
    firmwareBaseStatesQuery: any,
    toggleEditFirmwareModal: () => void,
    firmwareEditGetValues: UseFormGetValues<FirmwareBase>,
    firmwareEditReset: UseFormReset<FirmwareBase>,
    firmwareEditControl: Control<FirmwareBase, any, FirmwareBase>,
    errors:FieldErrors<FirmwareBase>,
    api: NotificationInstance,
    isValid: boolean,
    isDirty: boolean
}

const EditFirmwareBaseListModal: React.FC<Props> = ({
    showEditFirmwareModal,
    firmwareBaseStatesQuery,
    toggleEditFirmwareModal,
    firmwareEditGetValues,
    firmwareEditReset,
    firmwareEditControl,
    errors,
    api,
    isValid,
    isDirty
}: Props) => {

    const queryClient = useQueryClient()

    const editFirmwareBaseMutation = useMutation({
        mutationFn: (data: FirmwareBase) => {
            console.log("Editing Firmware")
            return axios.post(`${BASE_URL}update_firmware/${data.id}`, {
                "id": data.id,
                "version_name": data.version_name,
                "changelog": data.changelog,
                "commit_hash": data.commit_hash,
                "status": data.status
            })
        },
        onSuccess: () => {
            toggleEditFirmwareModal();
            firmwareEditReset();
            queryClient.invalidateQueries({ queryKey: ['baseFirmwareList'] })
            api.success({
                title: 'Firmware Base Updated',
                description: `Firmware Base ${firmwareEditGetValues().version_name} updated successfully.`,
                placement: 'bottomRight',
                showProgress: true
            });
        },
        onError: (error: any) => {
            api.error({
                title: 'Firmware Base Update Failed',
                description: `Failed to update Firmware Base ${error.message}`,
                placement: 'bottomRight',
                showProgress: true
            });
        }
    });

    return(
        <Modal
            open={showEditFirmwareModal}
            onCancel={toggleEditFirmwareModal}
            title="Edit Firmware"
            width={760}
            closable={!editFirmwareBaseMutation.isPending}
            mask={{ closable: !editFirmwareBaseMutation.isPending }}
            footer={[
                <Button type="default" onClick={toggleEditFirmwareModal} disabled={editFirmwareBaseMutation.isPending}>Close</Button>,
                <Button
                    type="primary"
                    onClick={ () => {editFirmwareBaseMutation.mutate(firmwareEditGetValues())}}
                    loading={editFirmwareBaseMutation.isPending}
                    disabled={!isValid || !isDirty || editFirmwareBaseMutation.isPending}
                >
                    Save Changes
                </Button>
            ]}
        >
            <Form layout="vertical" preserve={false}>
                <Form.Item
                    label="Firmware Version Name"
                    validateStatus={errors.version_name ? "error" : ""}
                    help={errors.version_name?.message}
                >
                    <Controller
                        name="version_name"
                        control={firmwareEditControl}
                        rules={{ required: "Firmware Version Name is required" }}
                        render={({ field }) =>(
                            <Input
                                {...field}
                                placeholder="Enter Firmware Version Name"
                            />
                        )}
                    />
                </Form.Item>
                <Form.Item
                    label="Commit Hash"
                    validateStatus={errors.commit_hash ? "error" : ""}
                    help={errors.commit_hash?.message}
                >
                    <Controller
                        name="commit_hash"
                        control={firmwareEditControl}
                        rules={{ required: "Commit Hash is required" }}
                        render={({ field }) =>(
                            <Input
                                {...field}
                                placeholder="Enter Commit Hash"
                            />
                        )}
                    />
                </Form.Item>
                <Form.Item
                    label="Changelog / Patch Notes"
                    validateStatus={errors.changelog ? "error" : ""}
                    help={errors.changelog?.message}
                >
                    <Controller
                        name="changelog"
                        control={firmwareEditControl}
                        rules={{ required: "Changelog is required" }}
                        render={({ field }) =>(
                            <PatchNotesEditor
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </Form.Item>
                <Form.Item label="Select the firmware state" >
                    <Controller
                        name="status"
                        control={firmwareEditControl}
                        rules={{ required: true }}
                        render={({ field }) =>(
                            <Select
                                {...field}
                                options={firmwareBaseStatesQuery.data}
                                value={firmwareBaseStatesQuery.data?.find((state: { value: string }) => state.value === field.value)}
                                onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                            />
                        )}
                    />
                </Form.Item>
            </Form>
        </Modal>
    )
}
export default EditFirmwareBaseListModal;